"use client";
import Color from '@tiptap/extension-color';
import LinkExtension from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo,
  Underline as UnderlineIcon,
  Undo,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import LinkModal from './LinkModal';

interface FAQAnswerEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  error?: string;
}

export default function FAQAnswerEditor({
  value,
  onChange,
  placeholder: _placeholder = 'Provide a comprehensive answer to the question...',
  error
}: FAQAnswerEditorProps) {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [initialLinkUrl, setInitialLinkUrl] = useState<string>('');
  const [initialLinkRel, setInitialLinkRel] = useState<string | null>(null);
  const [initialOpenInNewTab, setInitialOpenInNewTab] = useState<boolean>(true);
  const [editorReady, setEditorReady] = useState(false);
  const editorRef = useRef<Editor | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-gray-300 pl-4 italic my-2',
          },
        },
      }),
      TextStyle,
      Color,
      Underline,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[150px] px-4 py-3',
      },
      handleKeyDown: (_view, event: KeyboardEvent) => {
        // Link: Ctrl+K or Cmd+K
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
          event.preventDefault();
          const editorInstance = editorRef.current;
          if (editorInstance) {
            const attrs = editorInstance.getAttributes('link');
            setInitialLinkUrl((attrs?.href as string) || '');
            // Properly handle rel attribute: if it exists and is 'nofollow', use it, otherwise null (dofollow)
            const relValue = attrs?.rel;
            setInitialLinkRel(relValue === 'nofollow' ? 'nofollow' : null);
            setInitialOpenInNewTab(((attrs?.target as string) || '') === '_blank');
            setIsLinkModalOpen(true);
          } else {
            // Fallback: open modal with empty values
            setInitialLinkUrl('');
            setInitialLinkRel(null);
            setInitialOpenInNewTab(true);
            setIsLinkModalOpen(true);
          }
          return true;
        }
        return false;
      },
    },
    immediatelyRender: false,
    onCreate: ({ editor }) => {
      editorRef.current = editor;
      setEditorReady(true);
    },
  });

  // Keep editorRef in sync
  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
    }
  }, [editor]);

  // Update editor content when value prop changes (for loading existing FAQs)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  const openLinkModal = () => {
    const editorInstance = editorRef.current || editor;
    if (!editorInstance || !editorReady) {
      alert('Editor is not ready yet. Please wait a moment.');
      return;
    }
    const attrs = editorInstance.getAttributes('link');
    setInitialLinkUrl((attrs?.href as string) || '');
    // Properly handle rel attribute: if it exists and is 'nofollow', use it, otherwise null (dofollow)
    const relValue = attrs?.rel;
    setInitialLinkRel(relValue === 'nofollow' ? 'nofollow' : null);
    setInitialOpenInNewTab(((attrs?.target as string) || '') === '_blank');
    setIsLinkModalOpen(true);
  };

  const onConfirmLink = ({ url, rel, openInNewTab }: { url: string; rel: string | null; openInNewTab: boolean }) => {
    try {
      const editorInstance = editorRef.current || editor;
      if (!editorInstance) {
        alert('Editor is not ready');
        return;
      }
      // Build attributes object - always include rel to properly remove it when null (dofollow)
      const attrs: { href: string; target?: string | null; rel?: string | null } = {
        href: url
      };

      // Explicitly set rel: null removes the attribute (dofollow), 'nofollow' sets it
      attrs.rel = rel;

      // Handle target attribute
      if (openInNewTab) {
        attrs.target = '_blank';
      } else {
        // Explicitly remove target if not opening in new tab
        attrs.target = null;
      }

      editorInstance.chain().focus().extendMarkRange('link').setLink(attrs).run();
    } catch (e) {
      console.error('Error inserting link:', e);
      alert('Failed to insert link');
    } finally {
      setIsLinkModalOpen(false);
    }
  };

  if (!editor) {
    return (
      <div className="border border-gray-300 rounded-md p-4">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading editor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`border rounded-md ${error ? 'border-red-500' : 'border-gray-300'}`}>
        {/* Toolbar */}
        <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 flex-wrap">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
              title="Bold (Ctrl+B)"
            >
              <Bold className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
              title="Italic (Ctrl+I)"
            >
              <Italic className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
              title="Bullet List"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
              title="Numbered List"
            >
              <ListOrdered className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('blockquote') ? 'bg-gray-300' : ''}`}
              title="Quote"
            >
              <Quote className="h-4 w-4" />
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={openLinkModal}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${editor.isActive('link') ? 'bg-gray-300' : ''}`}
              title="Insert Link (Ctrl+K)"
            >
              <LinkIcon className="h-4 w-4" />
            </button>
            {editor.isActive('link') && (
              <button
                type="button"
                onClick={() => editor.chain().focus().unsetLink().run()}
                className="p-2 rounded hover:bg-red-100 text-red-600 transition-colors"
                title="Remove Link"
              >
                <LinkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <Redo className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Editor Content */}
        <div className="relative min-h-[150px] max-h-[400px] overflow-y-auto">
          {!editorReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600 text-sm">Loading editor...</p>
              </div>
            </div>
          )}
          <EditorContent editor={editor} />
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Link Modal */}
      <LinkModal
        isOpen={isLinkModalOpen}
        initialUrl={initialLinkUrl}
        initialRel={initialLinkRel}
        initialOpenInNewTab={initialOpenInNewTab}
        onCancel={() => setIsLinkModalOpen(false)}
        onConfirm={onConfirmLink}
      />
    </div>
  );
}

